<template>
  <div class="ca-root">
    <div class="nc-surface ca-head">
      <button class="nc-btn nc-btn-ghost nc-btn-sm" @click="$emit('back')">← Back</button>
      <div class="ca-title">
        <h2>{{ campaign.opportunity_name || 'Campaign' }}</h2>
        <div class="ca-subtitle">
          {{ campaign.country_name || '—' }} · {{ campaign.customer_name || 'No customer' }} ·
          {{ windowText }}
          <span v-if="triggerText"> · {{ triggerText }}</span>
        </div>
      </div>
      <span class="nc-badge" :class="statusBadgeClass">{{ statusLabel }}</span>
      <button class="nc-btn nc-btn-ghost nc-btn-sm" @click="$emit('refresh')">
        <i class="fa fa-refresh"></i> Refresh
      </button>

      <div class="ca-export">
        <button class="nc-btn nc-btn-primary nc-btn-sm" @click="exportOpen = !exportOpen">
          <i class="fa fa-download"></i> Export
        </button>
        <div v-if="exportOpen" class="nc-surface ca-export-menu">
          <button class="ca-export-item" @click="downloadReport">
            <strong>Report (PDF)</strong>
            <span>The nudge preview, headline numbers, per-button responses, funnel and
              trend on printable pages. Choose "Save as PDF" in the print window.</span>
          </button>
          <button class="ca-export-item" @click="runExport">
            <strong>Response summary (CSV)</strong>
            <span>One row per user: delivered, replied, reply time, what they replied into, and
              every funnel stage they reached.</span>
          </button>
          <button class="ca-export-item" @click="openTranscripts">
            <strong>Reply transcripts</strong>
            <span>Full conversation threads for a sample of nudged users, uploaded to Drive.
              Pick how many and which users.</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Stat tiles, not a chart: five independent single numbers with no shared scale.
         Values use proportional figures — tabular-nums loosens digits at this size. -->
    <div class="ca-tiles">
      <div class="nc-surface ca-tile">
        <div class="ca-tile-label">Nudges sent</div>
        <div class="ca-tile-value">{{ totals.attempts.toLocaleString() }}</div>
        <div class="ca-tile-sub">{{ totals.unique_users.toLocaleString() }} distinct users</div>
      </div>
      <div class="nc-surface ca-tile">
        <div class="ca-tile-label">Delivered</div>
        <div class="ca-tile-value">{{ totals.delivered.toLocaleString() }}</div>
        <div class="ca-tile-sub">{{ deliveryRate }}% delivery</div>
      </div>
      <div class="nc-surface ca-tile">
        <div class="ca-tile-label">Replies</div>
        <div class="ca-tile-value">{{ totals.responded.toLocaleString() }}</div>
        <div class="ca-tile-sub">
          {{ replyRate }}% of {{ totals.delivered.toLocaleString() }} delivered
        </div>
      </div>
      <div class="nc-surface ca-tile">
        <div class="ca-tile-label">Failed</div>
        <div class="ca-tile-value">{{ totals.failed.toLocaleString() }}</div>
        <div class="ca-tile-sub">{{ failureRate }}% of attempts</div>
      </div>
      <div class="nc-surface ca-tile">
        <div class="ca-tile-label">Variants</div>
        <div class="ca-tile-value">{{ variants.length || '—' }}</div>
        <div class="ca-tile-sub">{{ variants.length > 1 ? 'A/B test running' : 'Single version' }}</div>
      </div>
    </div>

    <!-- The nudge as the user sees it. Template-backed groups render the approved Turn
         template (header, body, footer, buttons); anything else renders the saved text.
         Once there are send results each group's message sits in its Content group card
         below, next to its numbers, so this only shows for a campaign with no sends yet. -->
    <section v-if="previewGroups.length && !variants.length" class="nc-surface ca-panel">
      <header class="ca-panel-head">
        <h3>Nudge preview</h3>
        <p>What the user receives, in each language.</p>
      </header>
      <div v-for="group in previewGroups" :key="group.groupId" class="ca-preview-group">
        <div v-if="previewGroups.length > 1" class="ca-preview-group-name">
          {{ group.groupName || `Group ${group.groupId}` }}<span v-if="group.splitPercent"> · {{ group.splitPercent }}% of traffic</span>
        </div>
        <div class="ca-preview-grid">
          <nudge-bubble v-for="l in group.languages" :key="l.language" :message="l" />
        </div>
      </div>
    </section>

    <!-- One row per button, with how many responses landed in its destination. -->
    <section v-if="buttonGroups.length" class="nc-surface ca-panel">
      <header class="ca-panel-head">
        <h3>Button responses</h3>
        <p>{{ buttonCaption }}</p>
      </header>
      <div v-for="group in buttonGroups" :key="group.groupId" class="ca-btn-group">
        <div v-if="buttonGroups.length > 1" class="ca-preview-group-name">{{ group.groupName || `Group ${group.groupId}` }}</div>
        <table class="ca-btn-table">
          <thead>
            <tr>
              <th>Button</th><th>Goes to</th>
              <th class="ca-num">Responses</th><th class="ca-num">Users</th>
              <th class="ca-num">Share</th><th class="ca-num">% of delivered</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="b in group.buttons" :key="b.position">
              <td><strong>{{ b.position }}. {{ b.label }}</strong></td>
              <td>
                {{ b.destinationLabel }}
                <span v-if="b.sharedDestination" class="ca-btn-warn">same destination as an earlier button, counted there</span>
              </td>
              <td class="ca-num">{{ b.clicks.toLocaleString() }}</td>
              <td class="ca-num">{{ b.users.toLocaleString() }}</td>
              <td class="ca-num">{{ percentage(b.clicks, group.clicks) }}%</td>
              <td class="ca-num">{{ percentage(b.clicks, deliveredOfGroup(group.groupId)) }}%</td>
            </tr>
            <tr class="ca-btn-total">
              <td colspan="2">All buttons</td>
              <td class="ca-num">{{ group.clicks.toLocaleString() }}</td>
              <td class="ca-num"></td><td class="ca-num"></td>
              <td class="ca-num">{{ percentage(group.clicks, deliveredOfGroup(group.groupId)) }}%</td>
            </tr>
          </tbody>
        </table>
        <p class="ca-panel-note">
          {{ group.otherReplies.toLocaleString() }} other {{ group.otherReplies === 1 ? 'reply' : 'replies' }}:
          typed messages that didn't route to a button's destination, out of
          {{ group.totalReplies.toLocaleString() }} total.
        </p>
      </div>
      <p class="ca-panel-note">
        A response is counted when a reply lands in the destination a button points to. A user
        who types that button's number lands in the same place, so it counts the same.
      </p>
    </section>

    <div class="ca-columns">
      <section class="nc-surface ca-panel">
        <header class="ca-panel-head">
          <h3>Milestone progression</h3>
          <p>
            Distinct users at each stage, as a share of everyone nudged<span v-if="funnelConfigured">.
            Funnel stages attributed within {{ funnel.attributionDays }} days of the first
            nudge</span>
          </p>
        </header>

        <!-- Meter bars: one hue, length carries the magnitude, track is a lighter step of
             the same ramp. Each stage is directly labelled, so nothing depends on colour. -->
        <div class="ca-funnel">
          <div v-for="stage in funnelStages" :key="stage.label" class="ca-stage">
            <span class="ca-stage-label">{{ stage.label }}</span>
            <span class="ca-stage-track">
              <span
                class="ca-stage-fill"
                :class="{ 'is-journey': stage.kind === 'journey' }"
                :style="{ width: `${stage.percent}%` }"
              ></span>
            </span>
            <span class="ca-stage-value">{{ stage.value.toLocaleString() }}</span>
            <span class="ca-stage-percent">{{ stage.percent }}%</span>
          </div>
        </div>

        <p v-if="funnelAllZero" class="ca-panel-note ca-panel-note-warn">
          Every funnel stage reads zero although {{ funnel.deliveredUsers.toLocaleString() }}
          users were delivered to. That is usually a mapping problem rather than a real
          result — check that Funnel Stage Mapping points at conversation stages these users
          actually pass through.
        </p>
        <p v-if="!funnelConfigured" class="ca-panel-note ca-panel-note-warn">
          This journey has no funnel stage mapping configured, so there are no milestone,
          referral or redemption stages to show. Configure it in Journey Settings → Funnel
          Stage Mapping and they appear here automatically.
        </p>
        <p v-else-if="funnel.unmappedStages.length" class="ca-panel-note ca-panel-note-warn">
          Not shown, because no conversation stage is mapped to them:
          {{ funnel.unmappedStages.join(', ') }}.
        </p>

        <p class="ca-panel-note">
          Everything here counts distinct users, not messages — the tiles above carry message
          volume. Funnel stages are floored so a later stage never exceeds an earlier one: the
          mapping is cumulative, but the raw per-stage counts aren't.
        </p>
      </section>

      <section class="nc-surface ca-panel">
        <header class="ca-panel-head ca-panel-head-row">
          <div>
            <h3>Reply rate trend</h3>
            <p>{{ trendCaption }}</p>
          </div>
          <div class="ca-range">
            <input v-model="localFrom" type="date" class="nc-input nc-input-sm" aria-label="Trend from" />
            <span class="ca-range-to">to</span>
            <input v-model="localTo" type="date" class="nc-input nc-input-sm" aria-label="Trend to" />
            <button class="nc-btn nc-btn-ghost nc-btn-sm" @click="applyRange">Apply</button>
            <button
              v-if="hasSeparateModifiedDate"
              class="nc-btn nc-btn-sm nc-btn-quiet"
              title="First 7 days after this campaign was last modified"
              @click="useModifiedRange"
            >Since last edit</button>
            <button v-if="rangeChanged" class="nc-btn nc-btn-sm nc-btn-quiet" @click="resetRange">Reset</button>
          </div>
        </header>

        <trend-chart :days="filledDays" />
      </section>
    </div>

    <!-- Count + cohort, then the account ids go to the Audience Builder's own
         /audience/exportTranscripts endpoint. Nothing about transcript generation is
         reimplemented here — that endpoint already builds the Drive folder and forks the
         uploader. -->
    <transition name="ca-modal-fade">
      <div v-if="transcriptOpen" class="ca-modal-overlay" @click.self="closeTranscripts">
        <div class="nc-surface ca-modal">
          <h4 class="ca-modal-title">Export reply transcripts</h4>
          <p class="ca-modal-body">
            Full conversation threads are uploaded to a Google Drive folder, which can take a
            few minutes. Users are sampled at random from the cohort you pick — reading the
            most recent few would tell you about last week rather than about the campaign.
          </p>

          <div class="ca-modal-field">
            <label for="ca-cohort">Which users</label>
            <nv-select id="ca-cohort" v-model="transcriptCohort" class="nc-input">
              <option value="replied">Replied users{{ cohortCountLabel('replied') }}</option>
              <option value="not_replied">Did not reply{{ cohortCountLabel('not_replied') }}</option>
              <option value="any">Any nudged user{{ cohortCountLabel('any') }}</option>
            </nv-select>
          </div>

          <div class="ca-modal-field">
            <label for="ca-count">How many transcripts</label>
            <input
              id="ca-count"
              v-model="transcriptCount"
              type="number"
              min="1"
              :max="transcriptMax"
              class="nc-input"
            />
            <span class="ca-modal-hint">Between 1 and {{ transcriptMax }}.{{ cohortCapHint }}</span>
          </div>

          <div class="ca-modal-actions">
            <button class="nc-btn nc-btn-ghost" @click="closeTranscripts">Cancel</button>
            <button class="nc-btn nc-btn-primary" :disabled="!transcriptValid" @click="submitTranscripts">
              Export transcripts
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Shown for a single group too, retitled. The group's template name and its own
         reply rate appear nowhere else on the page, so hiding the panel when there's one
         variant threw away real information just because there was nothing to compare. -->
    <section v-if="variants.length" class="nc-surface ca-panel">
      <header class="ca-panel-head">
        <h3>{{ isAbTest ? 'A/B variant results' : 'Content group' }}</h3>
        <p>{{ variantCaption }}</p>
      </header>

      <div class="ca-variants">
        <div
          v-for="(variant, i) in rankedVariants"
          :key="variant.group_id"
          class="ca-variant"
          :class="{ 'is-leading': variant.isLeading }"
        >
          <div class="ca-variant-head">
            <span class="ca-swatch" :style="{ background: seriesColor(i) }" aria-hidden="true"></span>
            <span class="ca-variant-name">{{ variant.group_name || `Group ${variant.group_id}` }}</span>
            <span v-if="variant.isLeading" class="nc-badge nc-badge-success">Leading</span>
            <span v-if="isAbTest" class="ca-variant-split">{{ variant.split_percent || 0 }}% of traffic</span>
          </div>
          <div class="ca-variant-rows">
            <div class="ca-variant-row">
              <span>Reply rate</span><strong>{{ rateOf(variant) }}%</strong>
            </div>
            <div class="ca-variant-row">
              <span>Users reached</span><strong>{{ variant.delivered_users.toLocaleString() }}</strong>
            </div>
            <div class="ca-variant-row">
              <span>Messages delivered</span><strong>{{ variant.delivered.toLocaleString() }}</strong>
            </div>
            <div class="ca-variant-row">
              <span>Nudges replied to</span><strong>{{ variant.responded.toLocaleString() }}</strong>
            </div>
            <div class="ca-variant-row">
              <span>Failed</span><strong>{{ variant.failed.toLocaleString() }}</strong>
            </div>
            <div class="ca-variant-row">
              <span>Template</span><strong>{{ variant.msg_template_name || 'Generated' }}</strong>
            </div>
          </div>

          <!-- The message this group sends, so its numbers can be read against its copy. -->
          <div v-if="variantLanguages(variant).length" class="ca-variant-message">
            <div class="ca-variant-message-head">
              <span>Message</span>
              <div v-if="variantLanguages(variant).length > 1" class="ca-lang-switch" role="tablist">
                <button
                  v-for="l in variantLanguages(variant)"
                  :key="l.language"
                  class="ca-lang-chip"
                  :class="{ 'is-active': l.language === variantMessage(variant).language }"
                  role="tab"
                  :aria-selected="l.language === variantMessage(variant).language"
                  :title="l.language"
                  @click="pickLanguage(variant.group_id, l.language)"
                >{{ l.alias || l.language }}</button>
              </div>
            </div>
            <nudge-bubble :message="variantMessage(variant)" />
          </div>
        </div>
      </div>

      <!-- Deliberately not calling a winner. Significance needs a test and a minimum
           sample, and neither is computed here — "Leading" states the observed fact. -->
      <p v-if="isAbTest" class="ca-panel-note">
        "Leading" is the higher observed reply rate, not a significance result — no
        confidence test is run on these numbers.
        <template v-if="variantsShareUsers">
          This campaign re-fires, so the split is applied per send and some accounts received
          both variants — the per-variant Users reached figures add up to more than the
          campaign's {{ totals.unique_users.toLocaleString() }}. The message counts and the
          rates above still partition exactly.
        </template>
      </p>
    </section>
  </div>
</template>

<script>
import api from '../../../../utils/api'
import TrendChart from './TrendChart.vue'
import NudgeBubble from './NudgeBubble.vue'
import { buildCampaignReportHtml, printCampaignReport } from './campaignReport'
import {
  STATUS_LABELS,
  STATUS_BADGE_CLASSES,
  describeSession,
  describeTrigger,
  percentage,
  isoOf,
  dayKey,
  addDays,
  daysBetween,
  todayIso
} from '../constants'

// Validated against the dataviz palette checks on a white surface: CVD ΔE 14.5 (protan),
// normal-vision ΔE 27.9, both ≥ 3:1 contrast. Assigned in fixed order, never cycled — a
// third variant would need the pair re-validated as a trio.
const SERIES = ['#00a0a0', '#eb6834']

const DEFAULT_TREND_DAYS = 7

// A hard ceiling on bars in one chart. Past this the axis is unreadable anyway, so a very
// wide hand-picked range truncates instead of rendering a smear.
const MAX_TREND_DAYS = 180

export default {
  name: 'campaign-analytics',
  components: { TrendChart, NudgeBubble },
  props: {
    campaign: { type: Object, required: true },
    totals: { type: Object, required: true },
    variants: { type: Array, default: () => [] },
    daily: { type: Array, default: () => [] },
    // { attributionDays, deliveredUsers, stages: [{key,label,users}], unmappedStages, configured }
    funnel: { type: Object, default: () => ({ stages: [], unmappedStages: [], configured: false, attributionDays: 30 }) },
    // { groups: [{groupId, groupName, buttons:[{position,label,destinationLabel,clicks,users,...}],
    //   clicks, totalReplies, otherReplies}], totalClicks, totalClickUsers, ... }
    buttons: { type: Object, default: () => ({ groups: [], totalClicks: 0, totalClickUsers: 0 }) },
    // { groups: [{groupId, groupName, splitPercent, languages:[{language, source, header, body, footer, buttons}]}] }
    preview: { type: Object, default: () => ({ groups: [] }) },
    exporting: { type: Boolean, default: false },
    // { replied, not_replied, any } — loaded lazily when the transcript modal opens.
    transcriptCohortSizes: { type: Object, default: null },
    from: { type: String, default: '' },
    to: { type: String, default: '' }
  },
  data () {
    return {
      localFrom: this.from,
      localTo: this.to,
      exportOpen: false,
      transcriptOpen: false,
      transcriptCohort: 'replied',
      transcriptCount: 25,
      transcriptMax: 100,
      // group id -> language picked in that group's Content group card
      pickedLanguage: {}
    }
  },
  computed: {
    statusLabel () {
      return STATUS_LABELS[this.campaign.opportunity_status] || 'Unknown'
    },
    statusBadgeClass () {
      return STATUS_BADGE_CLASSES[this.campaign.opportunity_status] || 'nc-badge-neutral'
    },
    windowText () {
      return describeSession(this.campaign)
    },
    triggerText () {
      const text = describeTrigger(this.campaign)
      return text === 'No inactivity trigger' ? '' : text
    },
    deliveryRate () {
      return percentage(this.totals.delivered, this.totals.attempts)
    },
    failureRate () {
      return percentage(this.totals.failed, this.totals.attempts)
    },
    // Nudges replied to ÷ nudges delivered — both MESSAGES. `responded_users` exists for the
    // funnel below, which is a headcount; using it here would divide people by messages.
    replyRate () {
      return percentage(this.totals.responded, this.totals.delivered)
    },
    funnelConfigured () {
      return Boolean(this.funnel && this.funnel.configured)
    },
    // PRD §3.4.2: Dispatched → Delivered → Replied → the journey's own funnel stages.
    // The first three come from "user-engagements" send rows; the rest come from
    // conversation_transition_logs via the journey's funnel_stage_mapping, so their labels
    // are whatever this journey actually configured — not a hardcoded
    // Referral/Redemption pair that only some journeys have.
    // Every stage counts PEOPLE, so the funnel actually narrows. It used to mix message
    // counts (dispatched, delivered) with user counts (replied, journey stages) on one
    // scale, which made "Replied" look like a collapse from 100% to 8% when in fact 3 of
    // the 4 users reached had replied. Message-level dispatch and delivery live in the
    // tiles above, where they aren't being compared against a headcount.
    funnelStages () {
      const base = this.totals.unique_users
      const delivery = [
        { label: 'Users nudged', value: this.totals.unique_users, kind: 'delivery' },
        { label: 'Users reached', value: this.totals.delivered_users, kind: 'delivery' },
        { label: 'Users replied', value: this.totals.responded_users, kind: 'delivery' }
      ]
      const journey = ((this.funnel && this.funnel.stages) || [])
        .map(stage => ({ label: stage.label, value: stage.users, kind: 'journey' }))
      return [...delivery, ...journey].map(stage => ({ ...stage, percent: percentage(stage.value, base) }))
    },
    transcriptValid () {
      const n = parseInt(this.transcriptCount, 10)
      return Number.isFinite(n) && n >= 1 && n <= this.transcriptMax
    },
    // Asking for more than the cohort holds isn't an error — you just get the whole cohort.
    // Saying so beforehand is friendlier than a surprise.
    cohortCapHint () {
      if (!this.transcriptCohortSizes) return ''
      const available = this.transcriptCohortSizes[this.transcriptCohort] || 0
      const wanted = parseInt(this.transcriptCount, 10)
      if (!Number.isFinite(wanted) || wanted <= available) return ''
      return ` This cohort has ${available.toLocaleString()}, so you'll get all of them.`
    },
    buttonGroups () {
      return (this.buttons && this.buttons.groups) || []
    },
    previewGroups () {
      return (this.preview && this.preview.groups) || []
    },
    buttonCaption () {
      const b = this.buttons || {}
      const clicks = b.totalClicks || 0
      return `${clicks.toLocaleString()} button response${clicks === 1 ? '' : 's'} from ${(b.totalClickUsers || 0).toLocaleString()} user${b.totalClickUsers === 1 ? '' : 's'}`
    },
    funnelAllZero () {
      const stages = (this.funnel && this.funnel.stages) || []
      if (!stages.length || !this.funnel.deliveredUsers) return false
      return stages.every(stage => !stage.users)
    },
    hasSeparateModifiedDate () {
      return Boolean(this.modifiedFrom && this.modifiedFrom !== this.defaultFrom)
    },
    modifiedFrom () {
      return isoOf(this.campaign.updated)
    },
    defaultFrom () {
      return isoOf(this.campaign.launched_at)
    },
    defaultTo () {
      const start = this.defaultFrom
      if (!start) return ''
      const today = todayIso()
      const seventh = addDays(start, DEFAULT_TREND_DAYS - 1)
      // Don't project the window past today — a campaign launched yesterday shouldn't
      // show six empty future days.
      return seventh > today ? today : seventh
    },
    rangeChanged () {
      return this.localFrom !== this.defaultFrom || this.localTo !== this.defaultTo
    },
    trendCaption () {
      if (!this.from) return 'Reply rate per day'
      const source = this.campaign.launched_from_creation ? 'created' : 'scheduled to start'
      const isDefault = this.from === this.defaultFrom && this.to === this.defaultTo
      const span = `${this.label(this.from)} – ${this.label(this.to)}`
      return isDefault
        ? `${span} — first ${DEFAULT_TREND_DAYS} days since this campaign was ${source}`
        : span
    },
    // Gap-fills the series so a day with no sends renders as a zero bar rather than
    // vanishing — otherwise five scattered sends across a month look like five
    // consecutive days.
    //
    // Counted with daysBetween and walked by index, not by advancing a cursor until it
    // passes `to`: a cursor loop depends on addDays actually moving forward, and when a
    // timezone bug stopped it doing so this ran to its iteration guard and drew 400 bars.
    // An index loop is bounded by construction.
    filledDays () {
      if (!this.from || !this.to) return this.daily
      const span = daysBetween(this.from, this.to)
      if (span < 1) return []
      const bySent = new Map(this.daily.map(row => [dayKey(row.day), row]))
      const out = []
      for (let i = 0; i < Math.min(span, MAX_TREND_DAYS); i++) {
        const day = addDays(this.from, i)
        if (!day) break
        const hit = bySent.get(day)
        out.push({
          day,
          delivered: hit ? hit.delivered : 0,
          responded: hit ? hit.responded : 0
        })
      }
      return out
    },
    isAbTest () {
      return this.variants.length > 1
    },
    // The same person can receive both variants when a campaign re-triggers, because the
    // split is decided per send. Detected rather than assumed, so the caveat only shows
    // when it actually applies.
    variantsShareUsers () {
      if (!this.isAbTest) return false
      const total = this.variants.reduce((sum, v) => sum + (v.delivered_users || 0), 0)
      return total > this.totals.unique_users
    },
    variantCaption () {
      const delivered = this.variants.reduce((sum, v) => sum + v.delivered, 0)
      if (!this.isAbTest) {
        const only = this.variants[0] || {}
        const template = only.msg_template_name || 'Generated content'
        return `${delivered.toLocaleString()} delivered · ${template} · no second variant`
      }
      return `${delivered.toLocaleString()} delivered across ${this.variants.length} variants`
    },
    // Only one variant is marked leading, and only when it's strictly ahead — a tie marks
    // neither, rather than silently favouring whichever group id came first.
    rankedVariants () {
      // Nothing leads when there's nothing to lead against — a lone group was being
      // badged "Leading", which reads as an A/B result that was never run.
      if (!this.isAbTest) {
        return this.variants.map(variant => ({ ...variant, isLeading: false }))
      }
      const rates = this.variants.map(v => percentage(v.responded, v.delivered))
      const best = Math.max(...rates, 0)
      const leaders = rates.filter(rate => rate === best && rate > 0).length
      return this.variants.map((variant, i) => ({
        ...variant,
        isLeading: leaders === 1 && rates[i] === best
      }))
    }
  },
  watch: {
    from (value) { this.localFrom = value },
    to (value) { this.localTo = value }
  },
  methods: {
    variantLanguages (variant) {
      const group = this.previewGroups.find(g => String(g.groupId) === String(variant.group_id))
      return group ? group.languages : []
    },
    // The picked language, else English, else the first one the group has.
    variantMessage (variant) {
      const languages = this.variantLanguages(variant)
      const picked = this.pickedLanguage[variant.group_id]
      return languages.find(l => l.language === picked) ||
        languages.find(l => l.alias === 'eng') ||
        languages[0]
    },
    pickLanguage (groupId, language) {
      this.$set(this.pickedLanguage, groupId, language)
    },
    seriesColor (index) {
      return SERIES[index % SERIES.length]
    },
    rateOf (variant) {
      return percentage(variant.responded, variant.delivered)
    },
    label (iso) {
      if (!iso) return ''
      return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    },
    // PRD §3.4 says the default window is "the first 7 days since the campaign was
    // launched or last modified". Those are two different anchors, so rather than guessing
    // which one a reader means, launch is the default and this is a one-click alternative.
    useModifiedRange () {
      const start = this.modifiedFrom
      if (!start) return
      const today = todayIso()
      const seventh = addDays(start, DEFAULT_TREND_DAYS - 1)
      this.localFrom = start
      this.localTo = seventh > today ? today : seventh
      this.applyRange()
    },

    percentage,

    deliveredOfGroup (groupId) {
      const variant = this.variants.find(v => String(v.group_id) === String(groupId))
      return variant ? variant.delivered : 0
    },

    runExport () {
      this.exportOpen = false
      this.$emit('export')
    },

    // Opens the browser's print window on a report built from what's already on screen, so
    // the PDF can't disagree with the page. "Save as PDF" there produces the file.
    async downloadReport () {
      this.exportOpen = false
      const name = this.campaign.opportunity_name || 'Nudge campaign'
      const title = `Nudge report - ${name} - ${todayIso()}`
      const preview = await this.previewWithImages()
      const html = buildCampaignReportHtml({
        title,
        campaign: this.campaign,
        totals: this.totals,
        variants: this.variants,
        buttons: this.buttons,
        preview,
        funnelStages: this.funnelStages,
        days: this.filledDays,
        windowText: this.windowText,
        triggerText: this.triggerText,
        statusLabel: this.statusLabel,
        trendCaption: this.trendCaption
      })
      printCampaignReport(html, title)
    },

    // The preview with each image header embedded as a data URL, so the printed report shows
    // the picture (the page itself loads it as a blob, which a print frame can't reuse).
    // Video and PDF headers stay a labelled line. A file that won't load just isn't embedded.
    async previewWithImages () {
      const preview = JSON.parse(JSON.stringify(this.preview || { groups: [] }))
      const languages = [].concat(...(preview.groups || []).map(g => g.languages || []))
      await Promise.all(languages.map(async l => {
        const media = l.headerMedia
        if (!media || media.format !== 'IMAGE' || !media.mediaId) return
        try {
          const { data, status } = await api.buildRequest('nudge-media-file', 'GET', {
            mediaId: media.mediaId, countryId: media.countryId, languageId: media.languageId
          }, null, 'blob')
          if (status !== 200) return
          media.dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result)
            reader.onerror = reject
            reader.readAsDataURL(data)
          })
        } catch (err) { /* report goes out with the label only */ }
      }))
      return preview
    },

    openTranscripts () {
      this.exportOpen = false
      this.transcriptOpen = true
      this.$emit('cohort-sizes')
    },

    closeTranscripts () {
      this.transcriptOpen = false
    },

    cohortCountLabel (cohort) {
      if (!this.transcriptCohortSizes) return ''
      return ` — ${(this.transcriptCohortSizes[cohort] || 0).toLocaleString()}`
    },

    submitTranscripts () {
      if (!this.transcriptValid) return
      this.transcriptOpen = false
      this.$emit('export-transcripts', {
        cohort: this.transcriptCohort,
        limit: parseInt(this.transcriptCount, 10)
      })
    },

    applyRange () {
      if (this.localFrom && this.localTo && this.localTo < this.localFrom) {
        this.$swal({ type: 'error', text: 'Trend end date must be on or after the start date' })
        return
      }
      this.$emit('range-change', { from: this.localFrom, to: this.localTo })
    },
    resetRange () {
      this.localFrom = this.defaultFrom
      this.localTo = this.defaultTo
      this.applyRange()
    }
  }
}
</script>

<style scoped src="../nudge-console-theme.css"></style>
<style scoped src="./CampaignAnalytics.css"></style>



// WEBPACK FOOTER //
// CampaignAnalytics.vue?11be3ef0